import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { notificationAPI } from '../services/api';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  // Return empty object with defaults if context is not available
  if (!context) {
    return {
      notifications: [],
      unreadCount: 0,
      newNotification: null,
      isConnected: false,
      markAsRead: () => {},
      markAllAsRead: () => {},
      clearNewNotification: () => {},
      requestNotificationPermission: () => {},
      fetchNotifications: () => {}
    };
  }
  return context;
};

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [newNotification, setNewNotification] = useState(null);
  // Notification sound on/off — persisted per browser
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('notificationSound') !== 'off');

  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem('notificationSound', next ? 'on' : 'off');
      return next;
    });
  };

  // Connect to Socket.IO when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user?._id) {
      const socketInstance = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        autoConnect: true
      });

      socketInstance.on('connect', () => {
        console.log('Socket connected');
        setIsConnected(true);
        // Join user's personal notification room
        socketInstance.emit('join', user._id);
      });

      socketInstance.on('disconnect', () => {
        console.log('Socket disconnected');
        setIsConnected(false);
      });

      // Listen for new notifications
      socketInstance.on('notification', (notification) => {
        console.log('New notification received:', notification);
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
        setNewNotification(notification);

        // Play sound if enabled
        if (notification.sound) {
          playNotificationSound();
        }

        // Show browser notification if permission granted
        showBrowserNotification(notification);

        // Clear the newNotification after animation
        setTimeout(() => setNewNotification(null), 5000);
      });

      setSocket(socketInstance);

      // Fetch existing notifications
      fetchNotifications();

      return () => {
        socketInstance.emit('leave', user._id);
        socketInstance.disconnect();
      };
    }
  }, [isAuthenticated, user?._id]);

  // Fetch notifications from server
  const fetchNotifications = useCallback(async () => {
    if (!user?._id) return;
    try {
      const response = await notificationAPI.getUserNotifications(user._id);
      setNotifications(response.data.notifications || []);
      const unread = (response.data.notifications || []).filter(n => !n.isRead).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [user?._id]);

  // Play notification sound
  const playNotificationSound = () => {
    try {
      if (localStorage.getItem('notificationSound') === 'off') return; // muted by user
      const audio = new Audio('/notification-sound.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Ignore autoplay errors
      });
    } catch (error) {
      // Ignore sound errors
    }
  };

  // Show browser notification
  const showBrowserNotification = (notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/logo192.png',
        tag: notification._id
      });
    }
  };

  // Request browser notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await notificationAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    if (!user?._id) return;
    try {
      await notificationAPI.markAllAsRead(user._id);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Clear new notification banner
  const clearNewNotification = () => {
    setNewNotification(null);
  };

  const value = {
    socket,
    notifications,
    unreadCount,
    isConnected,
    newNotification,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearNewNotification,
    requestNotificationPermission,
    soundEnabled,
    toggleSound
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
